import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  PlayCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/Skeleton";
import { runFullHealthCheck, type HealthReport, type HealthStatus } from "@/lib/healthChecks";
import { toast } from "sonner";

const STATUS_META: Record<HealthStatus, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  ok: { label: "OK", cls: "text-success border-success/40 bg-success/10", Icon: CheckCircle2 },
  warn: { label: "WARN", cls: "text-warning border-warning/40 bg-warning/10", Icon: AlertTriangle },
  error: { label: "ERROR", cls: "text-destructive border-destructive/40 bg-destructive/10", Icon: XCircle },
};

type TestResult = { name: string; status: "pass" | "fail"; detail?: string };

export default function AdminDashboard() {
  const [report, setReport] = useState<HealthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState<TestResult[] | null>(null);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await runFullHealthCheck();
      setReport(r);
    } catch (e) {
      toast.error("Falha ao rodar health check");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, []);

  // Inline smoke tests (rodam no browser, sem dependências)
  const runSmokeTests = async () => {
    setTesting(true);
    const results: TestResult[] = [];
    try {
      const { supabase } = await import("@/integrations/supabase/client");

      // T1: client inicializado
      results.push({
        name: "supabase client inicializado",
        status: supabase ? "pass" : "fail",
      });

      // T2: sessão presente (rota protegida)
      const { data: s } = await supabase.auth.getSession();
      results.push({
        name: "sessão autenticada",
        status: s.session ? "pass" : "fail",
        detail: s.session?.user.email ?? "no session",
      });

      // T3: RLS leitura agents
      const { error: agErr } = await supabase.from("agents").select("id").limit(1);
      results.push({
        name: "RLS SELECT agents",
        status: agErr ? "fail" : "pass",
        detail: agErr?.message,
      });

      // T4: RLS leitura chat_messages
      const { error: cmErr } = await supabase.from("chat_messages").select("id").limit(1);
      results.push({
        name: "RLS SELECT chat_messages",
        status: cmErr ? "fail" : "pass",
        detail: cmErr?.message,
      });

      // T5: marketplace público
      const { error: mkErr } = await supabase.from("marketplace_items").select("id").limit(1);
      results.push({
        name: "marketplace_items leitura pública",
        status: mkErr ? "fail" : "pass",
        detail: mkErr?.message,
      });

      // T6: edge function health-check responsiva
      const t0 = performance.now();
      const { data: hc, error: hcErr } = await supabase.functions.invoke("health-check");
      const dt = Math.round(performance.now() - t0);
      results.push({
        name: `edge function health-check (${dt}ms)`,
        status: !hcErr && hc?.overall ? "pass" : "fail",
        detail: hcErr?.message ?? `overall=${hc?.overall}`,
      });
    } catch (e) {
      results.push({
        name: "execução dos testes",
        status: "fail",
        detail: e instanceof Error ? e.message : String(e),
      });
    }
    setTests(results);
    setTesting(false);
    const failed = results.filter((r) => r.status === "fail").length;
    if (failed === 0) toast.success(`${results.length} testes passaram`);
    else toast.error(`${failed}/${results.length} testes falharam`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Voltar
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-display font-bold flex items-center gap-2">
              <Activity className="w-6 h-6 text-primary" /> Admin Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">
              Health checks e suíte de testes do P2HM Agents.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={refresh}
              disabled={loading}
              aria-label="Atualizar health checks"
              className="gap-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={runSmokeTests}
              disabled={testing}
              aria-label="Rodar suíte de testes"
              className="gap-1.5"
            >
              <PlayCircle className={`w-3.5 h-3.5 ${testing ? "animate-pulse" : ""}`} />
              Run tests
            </Button>
          </div>
        </div>

        {/* Overall status */}
        <div className="glass-panel rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm">Status geral</h2>
            {report && (
              <span className="text-[10px] text-muted-foreground">
                {new Date(report.timestamp).toLocaleString()}
              </span>
            )}
          </div>
          {loading && !report ? (
            <Skeleton className="h-12 w-full" />
          ) : report ? (
            <OverallBadge status={report.overall} count={report.checks.length} />
          ) : (
            <p className="text-xs text-muted-foreground">Sem dados.</p>
          )}
        </div>

        {/* Health checks list */}
        <div className="glass-panel rounded-lg border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h2 className="font-semibold text-sm">Health checks</h2>
            <p className="text-[11px] text-muted-foreground">
              Atualiza automaticamente a cada 60s. Combina checks client (sessão, RLS, realtime) e server (DB, vault, AI Gateway, pg_cron).
            </p>
          </div>
          <div className="divide-y divide-border">
            {loading && !report ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              report?.checks.map((c) => {
                const meta = STATUS_META[c.status];
                const Icon = meta.Icon;
                return (
                  <div
                    key={c.name}
                    className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-surface-2/30"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className={`w-4 h-4 shrink-0 ${meta.cls.split(" ")[0]}`} />
                      <div className="min-w-0">
                        <div className="font-mono text-xs">{c.name}</div>
                        {c.detail && (
                          <div className="text-[10px] text-muted-foreground truncate max-w-[420px]">
                            {c.detail}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {c.latency_ms}ms
                      </span>
                      <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>
                        {meta.label}
                      </Badge>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Test suite results */}
        {tests && (
          <div className="glass-panel rounded-lg border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="font-semibold text-sm">Suíte de testes</h2>
              <p className="text-[11px] text-muted-foreground">
                {tests.filter((t) => t.status === "pass").length}/{tests.length} passaram
              </p>
            </div>
            <div className="divide-y divide-border">
              {tests.map((t, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {t.status === "pass" ? (
                      <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-destructive shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-xs">{t.name}</div>
                      {t.detail && (
                        <div className="text-[10px] text-muted-foreground truncate max-w-[420px]">
                          {t.detail}
                        </div>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      t.status === "pass"
                        ? "text-success border-success/40 bg-success/10"
                        : "text-destructive border-destructive/40 bg-destructive/10"
                    }`}
                  >
                    {t.status.toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-[11px] text-muted-foreground p-3 border border-border rounded-lg">
          Para suítes mais profundas, rode <code className="font-mono">npm run test</code> (Vitest)
          localmente. Edge functions têm testes Deno em <code className="font-mono">supabase/functions/health-check/index_test.ts</code>.
        </div>
      </div>
    </div>
  );
}

function OverallBadge({ status, count }: { status: HealthStatus; count: number }) {
  const meta = STATUS_META[status];
  const Icon = meta.Icon;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-md border ${meta.cls}`}>
      <Icon className="w-6 h-6" />
      <div>
        <div className="font-bold text-lg">{meta.label}</div>
        <div className="text-[10px] opacity-80">{count} checks executados</div>
      </div>
    </div>
  );
}
